import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { setHours, setMinutes } from 'date-fns';
import { cn } from '@/utils/cn';
import { DateTimePickerPanel } from '@/components/ui/DateTimePickerPanel';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import {
  buildHourOptions,
  buildMinuteOptions,
  firstEnabledValue,
} from '@/components/ui/timePickerUtils';
import { usePickerPopover } from '@/components/ui/usePickerPopover';
import {
  displayDateTime,
  formatDateTimeLocal,
  parseDateTimeLocal,
} from '@/utils/dateInput';
import { utcInstantToCompanyPicker } from '@/utils/format';

export interface DateTimePickerProps {
  label?: string;
  error?: string;
  description?: string;
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

export function DateTimePicker({
  label,
  error,
  description: _description,
  value = '',
  onChange,
  min,
  max,
  disabled,
  required,
  placeholder: placeholderProp,
  className,
  id,
  name,
}: DateTimePickerProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('picker.selectDateTime');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { panelRef, panelCoords } = usePickerPopover(open, triggerRef, {
    estimatedHeight: 400,
    minWidth: 288,
    maxWidth: 320,
  });
  const parsed = parseDateTimeLocal(value);
  const [viewMonth, setViewMonth] = useState(() => parsed ?? new Date());
  const [draftDate, setDraftDate] = useState<Date | null>(parsed);
  const [hour, setHour] = useState(() => (parsed ? String(parsed.getHours()).padStart(2, '0') : '09'));
  const [minute, setMinute] = useState(() => (parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00'));

  useEffect(() => {
    if (!open) return;
    const next = parseDateTimeLocal(value);
    if (next) {
      setViewMonth(next);
      setDraftDate(next);
      setHour(String(next.getHours()).padStart(2, '0'));
      setMinute(String(next.getMinutes()).padStart(2, '0'));
    }
  }, [open, value]);

  useEffect(() => {
    const next = parseDateTimeLocal(value);
    if (next) {
      setViewMonth(next);
      setDraftDate(next);
      setHour(String(next.getHours()).padStart(2, '0'));
      setMinute(String(next.getMinutes()).padStart(2, '0'));
    }
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [panelRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const display = useMemo(() => displayDateTime(value), [value]);

  const hourOptions = useMemo(
    () => buildHourOptions(draftDate, min, max),
    [draftDate, min, max],
  );

  const minuteOptions = useMemo(
    () => buildMinuteOptions(draftDate, Number(hour), min, max),
    [draftDate, hour, min, max],
  );

  useEffect(() => {
    if (!open || !draftDate) return;
    if (!hourOptions.find((o) => o.value === hour && !o.disabled)) {
      setHour(firstEnabledValue(hourOptions, hour));
    }
  }, [open, draftDate, hourOptions, hour]);

  useEffect(() => {
    if (!open || !draftDate) return;
    if (!minuteOptions.find((o) => o.value === minute && !o.disabled)) {
      setMinute(firstEnabledValue(minuteOptions, minute));
    }
  }, [open, draftDate, minuteOptions, minute]);

  const composeDateTime = useCallback(
    (date: Date, hourValue: string, minuteValue: string) =>
      setMinutes(setHours(date, Number(hourValue)), Number(minuteValue)),
    [],
  );

  const commitDateTime = useCallback(
    (next: Date) => {
      const minDt = parseDateTimeLocal(min);
      const maxDt = parseDateTimeLocal(max);
      if (minDt && next < minDt) return false;
      if (maxDt && next > maxDt) return false;
      onChange?.(formatDateTimeLocal(next));
      return true;
    },
    [max, min, onChange],
  );

  const apply = () => {
    if (!draftDate) return;
    if (commitDateTime(composeDateTime(draftDate, hour, minute))) {
      setOpen(false);
    }
  };

  const pickDate = (date: Date) => {
    setDraftDate(date);
  };

  const handleHourChange = (nextHour: string) => {
    setHour(nextHour);
  };

  const handleMinuteChange = (nextMinute: string) => {
    setMinute(nextMinute);
  };

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <button
        ref={triggerRef}
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        className={cn(
          'input flex w-full min-w-0 items-center gap-2 text-left',
          !display && 'text-slate-500 dark:text-slate-400',
          display && 'text-slate-900 dark:text-white',
          error && 'input-error',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <ScheduleIcon className="shrink-0 text-primary-600 dark:text-primary-400" sx={{ fontSize: 18 }} />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      {open &&
        panelCoords &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[300]"
            style={{ top: panelCoords.top, left: panelCoords.left, width: panelCoords.width }}
          >
            <DateTimePickerPanel
              dateValue={draftDate ? formatDateTimeLocal(draftDate).slice(0, 10) : ''}
              viewMonth={viewMonth}
              onViewMonthChange={setViewMonth}
              onDateSelect={pickDate}
              minDate={min?.slice(0, 10)}
              maxDate={max?.slice(0, 10)}
              hour={hour}
              minute={minute}
              onHourChange={handleHourChange}
              onMinuteChange={handleMinuteChange}
              hourOptions={hourOptions}
              minuteOptions={minuteOptions}
              onApply={apply}
              onCancel={() => setOpen(false)}
              applyDisabled={!draftDate}
            />
          </div>,
          document.body,
        )}
      {error && (
        <p className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function defaultDateTimeLocalValue(offsetHours = 1): string {
  const instant = new Date(Date.now() + offsetHours * 3_600_000);
  instant.setUTCMinutes(0, 0, 0);
  return utcInstantToCompanyPicker(instant.toISOString());
}
